import Backspace from "@mui/icons-material/Backspace";
import FilterList from "@mui/icons-material/FilterList";
import Search from "@mui/icons-material/Search";
import SwapVert from "@mui/icons-material/SwapVert";
import { Box, IconButton, InputAdornment, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import React, { useRef, useState } from "react";
import { useI18nContext } from "#/shared/hooks";
import { DebouncedInput } from "#/shared/ui/molecules/DebouncedInput";
import { useProjectBrowserContext } from "./ProjectBrowserContext";
import { ProjectBrowserFilters } from "./ProjectBrowserFilters";
type ProjectBrowserHeaderProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchInputRef?: React.RefObject<HTMLInputElement | null>;
};
type SortOption = {
  value: "title" | "difficulty" | "date" | "category";
  label: string;
  ascLabel: string;
  descLabel: string;
};
export const ProjectBrowserHeader: React.FC<ProjectBrowserHeaderProps> = ({
  searchValue,
  onSearchChange,
  searchInputRef
}) => {
  const {
    LL
  } = useI18nContext();
  const {
    sortBy,
    sortOrder,
    setSort,
    selectedDifficulties,
    showOnlyNew
  } = useProjectBrowserContext();
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const filterButtonRef = useRef<HTMLButtonElement>(null);
  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);

  // Create sort options with translations
  const SORT_OPTIONS: SortOption[] = [{
    value: "title",
    label: LL.SORT_TITLE(),
    ascLabel: LL.SORT_TITLE_ASC(),
    descLabel: LL.SORT_TITLE_DESC()
  }, {
    value: "difficulty",
    label: LL.SORT_DIFFICULTY(),
    ascLabel: LL.SORT_DIFFICULTY_ASC(),
    descLabel: LL.SORT_DIFFICULTY_DESC()
  }, {
    value: "date",
    label: LL.SORT_DATE(),
    ascLabel: LL.SORT_DATE_ASC(),
    descLabel: LL.SORT_DATE_DESC()
  }, {
    value: "category",
    label: LL.SORT_CATEGORY(),
    ascLabel: LL.SORT_CATEGORY_ASC(),
    descLabel: LL.SORT_CATEGORY_DESC()
  }];
  const handlePropagation = (ev: React.BaseSyntheticEvent) => {
    ev.stopPropagation();
  };
  const handleChange = (value: string) => {
    onSearchChange(value.trim());
  };
  const handleClear: React.MouseEventHandler<HTMLButtonElement> = ev_0 => {
    handlePropagation(ev_0);
    onSearchChange("");
  };
  const handleSortMenuOpen = (ev_1: React.MouseEvent<HTMLElement>) => {
    handlePropagation(ev_1);
    setSortMenuAnchor(ev_1.currentTarget);
  };
  const handleSortMenuClose = () => {
    setSortMenuAnchor(null);
  };
  const handleSortSelect = (option: SortOption, order: "asc" | "desc") => {
    // Combine sortBy and order into single value like "titleAsc", "difficultyDesc"
    const orderCapitalized = order.charAt(0).toUpperCase() + order.slice(1);
    const sortValue = `${option.value}${orderCapitalized}`;
    setSort(sortValue);
    handleSortMenuClose();
  };
  const handleFilterClick = () => {
    setFilterPopoverOpen(!filterPopoverOpen);
  };
  const handleFilterClose = () => {
    setFilterPopoverOpen(false);
  };
  const currentSortOption = SORT_OPTIONS.find(opt => opt.value === sortBy);
  const sortLabel = sortOrder === "asc" ? currentSortOption?.ascLabel : currentSortOption?.descLabel;
  const hasActiveFilters = selectedDifficulties.length > 0 || showOnlyNew;
  return <Box sx={{
    p: 2,
    borderBottom: 1,
    borderColor: "divider"
  }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center" onClick={handlePropagation}>
          <Box sx={{
          flex: 1
        }}>
            <DebouncedInput inputRef={searchInputRef} label={LL.SEARCH_PROJECTS()} title={LL.SEARCH_PROJECTS()} name="search-project-browser" size="small" fullWidth value={searchValue} onChange={handleChange} onClick={handlePropagation} onKeyDown={handlePropagation} sx={{
            pointerEvents: "auto"
          }} timeout={200} InputProps={{
            startAdornment: <InputAdornment position="start">
                    <Search sx={{
                color: "text.secondary",
                fontSize: 20
              }} />
                  </InputAdornment>
          }} />
          </Box>
          <IconButton title="Clear search input" size="small" onClick={handleClear} sx={{
          pointerEvents: "auto"
        }} aria-label="Clear search">
            <Backspace fontSize="small" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start">
          <Tooltip title={sortLabel || LL.SORT_BY()} arrow>
            <IconButton onClick={handleSortMenuOpen} size="small" aria-label={LL.SORT_BY()} aria-haspopup="true" aria-expanded={Boolean(sortMenuAnchor)} sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 1
          }}>
              <SwapVert fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Advanced filters" arrow>
            <IconButton ref={filterButtonRef} onClick={handleFilterClick} size="small" aria-label="Advanced filters" aria-haspopup="true" aria-expanded={filterPopoverOpen} sx={{
            border: 1,
            borderColor: hasActiveFilters ? "primary.main" : "divider",
            borderRadius: 1,
            backgroundColor: hasActiveFilters ? "primary.main" : "transparent",
            color: hasActiveFilters ? "primary.contrastText" : "inherit",
            "&:hover": {
              backgroundColor: hasActiveFilters ? "primary.dark" : "action.hover"
            }
          }}>
              <FilterList fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
      <Menu anchorEl={sortMenuAnchor} open={Boolean(sortMenuAnchor)} onClose={handleSortMenuClose} onClick={handlePropagation} slotProps={{
      paper: {
        onClick: handlePropagation
      }
    }}>
        {SORT_OPTIONS.map(option_0 => <MenuItem key={`${option_0.value}-asc`} selected={sortBy === option_0.value && sortOrder === "asc"} onClick={() => handleSortSelect(option_0, "asc")}>
            {option_0.ascLabel}
          </MenuItem>)}
        {SORT_OPTIONS.map(option_1 => <MenuItem key={`${option_1.value}-desc`} selected={sortBy === option_1.value && sortOrder === "desc"} onClick={() => handleSortSelect(option_1, "desc")}>
            {option_1.descLabel}
          </MenuItem>)}
      </Menu>
      <ProjectBrowserFilters anchorEl={filterButtonRef.current} open={filterPopoverOpen} onClose={handleFilterClose} />
    </Box>;
};